/**
 * Pure function to compute trading pattern metrics from trade history.
 *
 * Extracted for testability — no React/hooks dependencies.
 *
 * Story 8.5 — Task 1 / Task 6.2
 */

import type { TradeHistoryEntry } from "@repo/shared-types";

export type TradingPatternMetrics = {
	totalTrades: number;
	buyCount: number;
	sellCount: number;
	buyPercentage: number;
	sellPercentage: number;
	averageTradeSize: number;
	tradesLast24h: number;
	tradesPerHour: number;
};

export function computeTradingPatterns(
	trades: TradeHistoryEntry[],
	nowMs?: number
): TradingPatternMetrics {
	if (trades.length === 0) {
		return {
			totalTrades: 0,
			buyCount: 0,
			sellCount: 0,
			buyPercentage: 0,
			sellPercentage: 0,
			averageTradeSize: 0,
			tradesLast24h: 0,
			tradesPerHour: 0,
		};
	}

	const buyCount = trades.filter((t) => t.type === "buy").length;
	const sellCount = trades.filter((t) => t.type === "sell").length;
	const totalTrades = trades.length;

	const buyPercentage = Math.round((buyCount / totalTrades) * 100);
	const sellPercentage = 100 - buyPercentage;

	const totalEthAmount = trades.reduce((sum, t) => sum + t.ethAmount, 0);
	const averageTradeSize = totalEthAmount / totalTrades;

	const now = nowMs ?? Date.now();
	const oneDayAgo = now - 24 * 60 * 60 * 1000;
	const tradesLast24h = trades.filter((t) => new Date(t.timestamp).getTime() > oneDayAgo).length;
	const tradesPerHour = tradesLast24h > 0 ? Math.round((tradesLast24h / 24) * 10) / 10 : 0;

	return {
		totalTrades,
		buyCount,
		sellCount,
		buyPercentage,
		sellPercentage,
		averageTradeSize,
		tradesLast24h,
		tradesPerHour,
	};
}
